import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Turnstile from 'react-turnstile';
import { nanoid } from 'nanoid';
import { useLocation } from 'react-router-dom';
import DonutCard from './DonutCard.jsx';
import OrderSummary from './OrderSummary.jsx';
import PricingInfo from './PricingInfo.jsx';

export default function OrderForm() {
  const { t } = useTranslation();
  const location = useLocation();

  const [name, setName] = useState('');
  const [chocolateQty, setChocolateQty] = useState(0);
  const [vanillaQty, setVanillaQty] = useState(0);
  const [pickup, setPickup] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const query = new URLSearchParams(location.search);
  const cancel = query.get('cancel');
  const canceledOrderId = query.get('order_id');

  // Restore form data
  useEffect(() => {
    const saved = localStorage.getItem('orderFormData');
    if (saved) {
      const data = JSON.parse(saved);
      setName(data.name || '');
      setChocolateQty(data.chocolateQty || 0);
      setVanillaQty(data.vanillaQty || 0);
      setPickup(data.pickup || '');
      setEmail(data.email || '');
      setNotes(data.notes || '');
    }
  }, []);

  // Clean up unpaid order after cancel
  useEffect(() => {
    if (cancel && canceledOrderId) {
      fetch(`${process.env.REACT_APP_EXPRESS_URL}/api/order/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: canceledOrderId }),
      });

      // Clean up URL to avoid accidental re-delete
      window.history.replaceState(null, '', '/order');
    }
  }, [cancel, canceledOrderId]);

  const orderId = `ORD-${nanoid(8).toUpperCase()}`;

  const totalQty = chocolateQty + vanillaQty;

  const minPickupDate = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }, []);

  const calculatePrice = (qty) => {
    if (qty >= 12) return qty * 3.0;
    if (qty >= 6) return qty * 3.25;
    return qty * 3.5;
  };

  const total = calculatePrice(totalQty);

  const handleReview = (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!name.trim()) return setError(t('order.error.name'));
    if (totalQty < 1 || totalQty > 100) return setError(t('order.error.totalQty'));
    if (!pickup || pickup < minPickupDate) return setError(t('order.error.pickupTime'));

    setPreviewMode(true);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    if (!captchaToken) {
      setLoading(false);
      setError('Please verify you are human before submitting.');
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_EXPRESS_URL}/api/order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderId,
          name,
          chocolate_qty: chocolateQty,
          vanilla_qty: vanillaQty,
          pickup_time: pickup,
          email,
          notes,
          total_price: total,
          captcha_token: captchaToken,
        }),
      });

      const result = await res.json();

      if (!res.ok || result.error) {
        setError(t(result.key || 'order.error.generic'));
      } else {
        setSuccess(true);
        setPreviewMode(false);
        setName('');
        setChocolateQty(0);
        setVanillaQty(0);
        setPickup('');
        setEmail('');
        setNotes('');

        // Save key form data before redirecting to Stripe
        localStorage.setItem(
          'orderFormData',
          JSON.stringify({
            name,
            chocolateQty,
            vanillaQty,
            pickup,
            email,
            notes,
          })
        );

        // 1. Call your backend to create Stripe Checkout session
        const stripeRes = await fetch(
          `${process.env.REACT_APP_EXPRESS_URL}/api/stripe/create-checkout-session`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [
                {
                  name: 'Donut Order',
                  price: Math.round(total * 100), // in cents
                  quantity: 1,
                },
              ],
              orderId: orderId,
            }),
          }
        );

        const stripeData = await stripeRes.json();
        if (stripeData.url) {
          window.location.href = stripeData.url;
        } else {
          setError('Payment link failed. Please try again.');
        }
      }
    } catch (err) {
      setError(t('order.error.generic'));
    }

    setLoading(false);
  };

  // --- FORM UI ---
  return previewMode ? (
    <div className="max-w-md mx-auto bg-white dark:bg-gray-800 p-6 rounded shadow">
      <h2 className="text-2xl font-bold text-pink-700 dark:text-pink-300 mb-4">
        {t('order.summary')}
      </h2>
      <p>
        <strong>{t('order.name')}:</strong> {name}
      </p>
      {email && (
        <p>
          <strong>{t('order.email')}:</strong> {email}
        </p>
      )}
      <p>
        <strong>{t('flavor.chocolate')}:</strong> {chocolateQty}
      </p>
      <p>
        <strong>{t('flavor.vanilla')}:</strong> {vanillaQty}
      </p>
      <p>
        <strong>{t('pickup_label')}:</strong> {pickup}
      </p>
      <p>
        <strong>{t('order.notes')}:</strong> {notes || t('order.none')}
      </p>
      <p className="mt-4">
        <strong>{t('total')}:</strong> {totalQty}
      </p>
      <p>
        <strong>{t('price')}:</strong> ${total.toFixed(2)}
      </p>

      {error && <p className="text-sm text-red-500 mt-2">{error}</p>}

      <div className="flex justify-between mt-6">
        <button
          type="button"
          onClick={() => setPreviewMode(false)}
          className="px-4 py-2 border border-pink-600 rounded text-pink-600 hover:bg-pink-100 dark:hover:bg-gray-700"
        >
          {t('order.edit')}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading}
          className="bg-pink-600 hover:bg-pink-700 text-white font-bold py-2 px-4 rounded"
        >
          {loading ? t('order.submitting') : t('order.confirm')}
        </button>
      </div>
    </div>
  ) : (
    <form
      onSubmit={handleReview}
      className="max-w-md mx-auto bg-white dark:bg-gray-800 p-6 rounded shadow"
    >
      <h2 className="text-2xl font-bold text-pink-700 dark:text-pink-300 mb-4">
        {t('order.title')} 🍩
      </h2>

      {/* name */}
      <div className="mb-4">
        <label className="block font-medium mb-1">{t('order.name')}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full p-2 border rounded
             bg-white text-black
             dark:bg-gray-700 dark:text-white"
        />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <DonutCard
          name={t('flavor.chocolate')}
          iname="chocolate"
          qty={chocolateQty}
          setQty={setChocolateQty}
        />
        <DonutCard
          name={t('flavor.vanilla')}
          iname="vanilla"
          qty={vanillaQty}
          setQty={setVanillaQty}
        />
      </div>

      <PricingInfo />

      <div className="mb-10">
        <OrderSummary chocolate={chocolateQty} vanilla={vanillaQty} total={total} />
      </div>

      {/* pickup */}
      <label className="block mb-1 font-medium">{t('pickup_label')}</label>
      <input
        type="date"
        min={minPickupDate}
        value={pickup}
        onChange={(e) => setPickup(e.target.value)}
        className="w-full mb-4 p-2 border rounded bg-white text-black dark:bg-gray-700 dark:text-white"
      />

      {/* email */}
      <div className="mb-4">
        <label className="block font-medium mb-1">{t('order.email')}</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full p-2 border rounded
             bg-white text-black
             dark:bg-gray-700 dark:text-white"
        />
      </div>

      {/* notes */}
      <div className="mb-4">
        <label className="block font-medium mb-1">{t('order.notes')}</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full p-2 border rounded
             bg-white text-black
             dark:bg-gray-700 dark:text-white"
        />
      </div>

      {error && <p className="text-sm text-red-500 mb-2">{error}</p>}
      {success && <p className="text-green-600 font-semibold mb-4">✅ {t('order.success')}</p>}
      <Turnstile
        sitekey={process.env.REACT_APP_TURNSTILE_SITEKEY || '1x00000000000000000000AA'}
        onVerify={(token) => setCaptchaToken(token)}
        className="mb-4"
      />
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-pink-600 text-white font-bold py-2 rounded"
      >
        {t('order.review')}
      </button>
    </form>
  );
}
